import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { LayoutDashboard, Mail, FolderOpen, Bell, Settings, Building2, ChevronLeft, ChevronRight, BarChart3, FileText, CalendarDays } from 'lucide-react'
import { cn } from '../atoms'
import { useDashboardSummary } from '../../hooks/useDashboard'
import { useCompany } from '../../hooks/useCompany'
import { useImpersonation } from '../../store/impersonation.store'
import { getAssetUrl } from '../../utils/assetUrl'
import { useAuth } from '../../store/auth.store'
import { useRegulatoryEvents } from '../../hooks/useRegulatoryCalendar'

import { useUnseenNotifications } from '../../hooks/useUnseenNotifications'

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Comunicaciones', href: '/emails', icon: Mail },
  { name: 'Casos', href: '/cases', icon: FolderOpen },
  { name: 'Alertas', href: '/alerts', icon: Bell },
  { name: 'Calendario', href: '/calendar', icon: CalendarDays },
]

const analyticsNavigation = [
  { name: 'Reportes', href: '/reports', icon: FileText },
  { name: 'Analítica y gráficas', href: '/analytics', icon: BarChart3 },
]

const settingsNavigation = [
  { name: 'Configuración', href: '/settings', icon: Settings },
]

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const { impersonateMailboxId } = useImpersonation()
  const { data: dashboard } = useDashboardSummary(impersonateMailboxId)
  const { data: company } = useCompany()
  const { user } = useAuth()
  const { data: regulatoryEvents } = useRegulatoryEvents()
  const { unseenAlertsCount, unseenCalendarCount, markAlertsAsSeen, markCalendarAsSeen } = useUnseenNotifications()
  const [showCalendarPreview, setShowCalendarPreview] = useState(false)
  const alertCount = unseenAlertsCount
  const upcomingRegulatoryEvents = (regulatoryEvents || []).filter(event => event.computedStatus !== 'COMPLETED' && event.computedStatus !== 'CANCELLED').slice(0, 5)
  const regulatoryEventCount = unseenCalendarCount

  return (
    <aside className={cn(
      'relative hidden md:flex md:flex-col bg-white border-r border-slate-200 transition-all duration-300 dark:bg-gray-900 dark:border-gray-700',
      collapsed ? 'w-16' : 'w-[296px]'
    )}>
      <div className={cn('relative flex items-center border-b border-slate-100 dark:border-gray-700', collapsed ? 'justify-center px-0 py-5' : 'justify-center px-6 py-6')}>
        {company?.logoUrl ? (
          <img
            src={getAssetUrl(company.logoUrl)}
            alt="Logo"
            className={cn(
              'object-contain flex-shrink-0',
              collapsed ? 'w-10 h-10' : 'w-[220px] h-[70px]'
            )}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <div className={cn('flex items-center gap-2 text-slate-900 dark:text-gray-100', collapsed && 'justify-center')}>
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-600 flex-shrink-0"><Building2 className="w-5 h-5 text-white" /></div>
            {!collapsed && <div><p className="text-xl font-bold leading-none">ComCenter</p><p className="mt-1 text-[10px] text-slate-400">Centro inteligente de Comunicaciones</p></div>}
          </div>
        )}
        {!collapsed && company?.logoUrl && <div className="sr-only">{company?.name || 'ComCenter'}</div>}
        {!collapsed ? (
          <button
            onClick={() => setCollapsed(!collapsed)}
             className="absolute right-3 top-3 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
            title="Colapsar menú"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            title="Expandir menú"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-5 overflow-y-auto">
        <div className="space-y-1">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/'}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl transition-colors',
                collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3 text-sm font-medium',
                isActive || (item.href === '/emails' && location.pathname === '/emails')
                   ? 'bg-blue-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                   : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
              )
            }
            title={collapsed ? item.name : undefined}
            onClick={() => { if (item.name === 'Calendario') setShowCalendarPreview(current => !current) }}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <>{item.name}</>}
            {!collapsed && item.name === 'Alertas' && alertCount > 0 && (
              <span className="ml-auto flex items-center justify-center min-w-[22px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                {alertCount}
              </span>
            )}
            {collapsed && item.name === 'Alertas' && alertCount > 0 && (
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
            )}
            {!collapsed && item.name === 'Calendario' && regulatoryEventCount > 0 && (
              <span className="ml-auto flex h-5 min-w-[22px] items-center justify-center rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700">{regulatoryEventCount}</span>
            )}
            {collapsed && item.name === 'Calendario' && regulatoryEventCount > 0 && (
              <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-amber-500" />
            )}
          </NavLink>
        ))}
        </div>

        {!collapsed && <p className="px-4 pb-2 pt-7 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Analítica</p>}
        <div className="space-y-1">
        {analyticsNavigation.map((item) => (
          <NavLink key={item.name} to={item.href} className={cn('flex items-center rounded-xl transition-colors', collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3 text-sm font-medium', location.pathname === '/analytics' && item.name === 'Analítica y gráficas' ? 'bg-blue-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-gray-800')} title={collapsed ? item.name : undefined}>
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {!collapsed && item.name}
          </NavLink>
        ))}
        </div>

        {!collapsed && <p className="px-4 pb-2 pt-7 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Configuración</p>}
        <div className="space-y-1">
        {settingsNavigation.map((item) => (
          <NavLink key={item.name} to={item.href} className={({ isActive }) => cn('flex items-center rounded-xl transition-colors', collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3 text-sm font-medium', isActive && item.name === 'Configuración' ? 'bg-blue-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-gray-800')} title={collapsed ? item.name : undefined}>
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {!collapsed && item.name}
          </NavLink>
        ))}
        </div>
      </nav>

      {showCalendarPreview && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowCalendarPreview(false)} />
          <div className={cn('fixed z-40 w-[340px] rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900', collapsed ? 'left-[72px]' : 'left-[304px]', 'bottom-24')}>
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-gray-700">
              <div><h3 className="text-sm font-bold text-slate-900 dark:text-gray-100">Próximos vencimientos</h3><p className="mt-0.5 text-[11px] text-slate-400">Eventos regulatorios</p></div>
              <CalendarDays className="h-5 w-5 text-primary-600" />
            </div>
            <div className="max-h-80 overflow-y-auto">
              {upcomingRegulatoryEvents.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500">No hay vencimientos próximos</p>
              ) : upcomingRegulatoryEvents.map(event => (
                <div key={event.id} className="flex items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-0 dark:border-gray-800">
                  <span className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', event.computedStatus === 'OVERDUE' ? 'bg-red-500' : 'bg-primary-500')} />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-slate-800 dark:text-gray-100">{event.title}</p><p className="mt-0.5 truncate text-xs text-slate-500">{event.entity.acronym || event.entity.name}</p><p className={cn('mt-1 text-xs font-medium', event.computedStatus === 'OVERDUE' ? 'text-red-600' : 'text-slate-500')}>{new Date(event.nextDueDate).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })} · {event.computedStatus === 'OVERDUE' ? 'Vencido' : 'Pendiente'}</p></div>
                </div>
              ))}
            </div>
            <NavLink onClick={() => setShowCalendarPreview(false)} to="/calendar" className="block border-t border-slate-100 px-4 py-3 text-center text-sm font-semibold text-primary-600 hover:bg-primary-50 dark:border-gray-700">Ver calendario completo</NavLink>
          </div>
        </>
      )}

      <div className={cn('border-t border-slate-100 dark:border-gray-700', collapsed ? 'px-2 py-4' : 'px-3 py-4')}>
        <div className={cn('flex items-center rounded-xl', collapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2')}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          {!collapsed && <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-800 dark:text-gray-100">{user?.firstName} {user?.lastName}</p><p className="text-[11px] uppercase tracking-wide text-slate-400">{user?.role || 'Usuario'}</p></div>}
          {!collapsed && <ChevronRight className="ml-auto h-4 w-4 rotate-90 text-slate-400" />}
        </div>
        {!collapsed && <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/60"><div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" /><span className="text-xs font-semibold text-emerald-600">En vivo</span></div><p className="mt-1 pl-4 text-[11px] text-slate-400">Actualizado ahora</p></div>}
      </div>
    </aside>
  )
}
